import React, {useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {Button} from '@tickchak/react-share/app';
import * as css from './VideoPopup.css';
import Video from '../../../component/video/Video';
import ShareButton from '../ShareButton';
import FeedbackSection from '../FeedbackSection/FeedbackSection';
import RelatedVideos from '../RelatedVideos/RelatedVideos';
import {TSupportVideos} from '../../../redux/management/management.type';
import {TState} from '../../../redux/store.type';
import MobilePopup from '../../../component/newPopup/mobilePopup/MobilePopup';
import PermissionBanner from '../PermissionBanner/PermissionBanner';
import SupportPerson from '../../../assets/icons/SupportPerson';
import Close from '../../../component/close/Close';
import MasterPermission from '../../../pages/components/rolePermission/MasterPermission';
import Pencil from '../../../assets/icons/Pencil';
import CustomScrollbar from '../../../component/customScrollbar/CustomScrollbar';

interface VideoPopupProps {
    video: TSupportVideos;
    onClose: () => void;
    openWidget: (e?: React.MouseEvent) => void;
    showClass: string;
    relatedVideos?: TSupportVideos[];
    onSelectRelated?: (video: TSupportVideos) => void;
    onEditVideo?: (videoId: number) => void;
}

function VideoPopup({video, onClose, openWidget, showClass, relatedVideos = [], onSelectRelated, onEditVideo}: VideoPopupProps) {
    const {t} = useTranslation('layout');
    const location = useLocation();
    const isLoggedIn = useSelector((s: TState) => s.auth.isLoggedIn);
    const videoContentRef = useRef<HTMLDivElement>(null);

    const shareUrl = useMemo(() => {
        if (!video?.id) return '';
        const params = new URLSearchParams(location.search);
        params.set('videoId', video.id.toString());
        return `${window.location.origin}${location.pathname}?${params.toString()}`;
    }, [location.pathname, location.search, video.id]);


    const handleEdit = () => {
        onEditVideo?.(video.id);
        onClose();
    };

    const customHeader = (
        <css.popupHeader>
            <css.headerEnd>
                <Close onClick={onClose} className="pop" />
                <css.headerTitle>{t('support')}</css.headerTitle>
            </css.headerEnd>
            <css.headerActions>
                <MasterPermission>
                    <Button
                        variant="brand"
                        size="category"
                        onClick={handleEdit}
                        iconEnd={<Pencil />}
                    >
                        {t('editGuide')}
                    </Button>
                </MasterPermission>
                <ShareButton url={shareUrl} title={video.title} />
            </css.headerActions>
        </css.popupHeader>
    );

    return (
        <MobilePopup 
            showClass={showClass} 
            close={onClose} 
            title={undefined}
            list={undefined} 
            forwardRef={undefined} 
            dataCyName="videoPopup" 
            TopComponent={customHeader}
            height="100%"
        >
            <>
            <CustomScrollbar contentRef={videoContentRef} cssStyle={'inset-inline-end: 3px;'} />
            <css.videoContentWrapper ref={videoContentRef}>
                {/* Video Title */}
                <css.videoTitle>{video.title}</css.videoTitle>
                
                {/* Video Player */}
                <css.videoWrapper>
                    <Video video={video.videoLink} />
                </css.videoWrapper>
                
                {/* Permissions Banner */}
                <PermissionBanner permissions={video.permission} />
                
                {/* Description */}
                {(video.description || video.summary) && (
                    <css.description>
                        {video.description || video.summary}
                    </css.description>
                )}
                
                {/* Related Videos Section */}
                <RelatedVideos videos={relatedVideos} onSelect={onSelectRelated} />
                
                {/* Feedback Section - Only show when logged in */}
                {isLoggedIn && (
                    <FeedbackSection videoId={video.id} />
                )}
            </css.videoContentWrapper>
            
            {/* Footer */}
            <css.popupFooter>
                <Button
                    variant="primary"
                    size="category"
                    onClick={onClose}
                    iconStart={<css.arrowIcon />}
                >
                    {t('backToList')}
                </Button>
                <Button
                    variant="brand"
                    size="category"
                    onClick={openWidget}
                    iconEnd={<SupportPerson />}
                >
                    {t('contactSupport')}
                </Button>
            </css.popupFooter>
            </>
        </MobilePopup>
    );
}

export default VideoPopup;
